/**
 * Beyond Sure — motion utilities.
 *
 * Centralizes:
 *   • the reduced-motion hook used by every animated component + GSAP timeline
 *   • the canonical easings + durations so motion stays consistent
 *
 * Anything that animates in this app should consume from here rather than
 * hard-coding magic curves at the call site.
 */
import { useEffect, useState } from "react";

const QUERY = "(prefers-reduced-motion: reduce)";

/** True when the user has asked the OS to minimize motion. Browser-only. */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia(QUERY);
    const handler = (event: MediaQueryListEvent) => setReduced(event.matches);
    // addEventListener is the modern API; Safari < 14 used addListener.
    if (mql.addEventListener) {
      mql.addEventListener("change", handler);
      return () => mql.removeEventListener("change", handler);
    }
    mql.addListener(handler);
    return () => mql.removeListener(handler);
  }, []);

  return reduced;
}

/** Canonical cubic-bezier curves used across components.
 *  Motion accepts these as `ease` arrays directly. */
export const EASE_STANDARD: [number, number, number, number] = [
  0.4, 0, 0.2, 1,
];
export const EASE_OUT_SOFT: [number, number, number, number] = [
  0.16, 1, 0.3, 1,
];
/** Spring-like cubic-bezier — overshoots slightly past 1.0.
 *  Used on premium entrance moments (form panel reveal) where a tiny
 *  overshoot reads as "alive" without the jelly feel of a true spring. */
export const EASE_SPRING: [number, number, number, number] = [
  0.34, 1.56, 0.64, 1,
];

/** Durations in seconds (Motion's unit, not CSS ms). */
export const DURATION = {
  fast: 0.16,
  base: 0.24,
  slow: 0.38,
  entrance: 0.6,
} as const;
